import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { Logo } from "@/components/brand/Logo";

export default function LoginPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { login, user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (user) {
      const next = params.get("next");
      if (next) nav(next);
      else nav(user.role === "admin" ? "/admin" : "/portal");
    }
  }, [user, nav, params]);

  const submit = async (e) => {
    e.preventDefault();
    const res = await login(email, password);
    if (!res.ok) toast.error(res.error);
    else toast.success("Welcome back!");
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background flex items-center" data-testid="login-page">
      <div className="container-x grid lg:grid-cols-2 gap-10 items-center py-10">
        <div className="hidden lg:block">
          <div className="card-clean hero-wash p-10 relative overflow-hidden min-h-[520px]">
            <Logo variant="lockup" height={48} to={null} />
            <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-xs text-muted-foreground">
              <KeyRound className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Sign in
            </div>
            <h1 className="mt-4 font-display text-4xl font-semibold leading-tight">
              Welcome back to <br /><span className="text-[hsl(var(--primary))]">AlfaDrive.</span>
            </h1>
            <p className="mt-4 text-muted-foreground max-w-md">Reserve vehicles, manage rentals, upload documents, and access member pricing across Ontario.</p>
          </div>
        </div>

        <div className="max-w-md w-full mx-auto lg:mx-0">
          <h2 className="font-display text-3xl font-semibold">Sign in</h2>
          <p className="text-muted-foreground mt-1">Access your AlfaDrive account.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@alfadrive.ca" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password-input" />
            </div>
            <Button type="submit" className="w-full h-11 rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]" disabled={loading} data-testid="login-submit-button">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
            <p className="text-sm text-muted-foreground text-center">New to AlfaDrive? <Link to="/signup" className="underline underline-offset-4">Create an account</Link></p>
          </form>
        </div>
      </div>
    </div>
  );
}
